"use client";

import { MessageSquare, Plus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import {
  deleteConversation,
  LinkedShortlistError,
  listConversations,
  type ConversationSummary,
} from "@/lib/api/recruiter";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ConversationListProps {
  currentConversationId: number | null;
  onSelectConversation: (id: number) => void;
  onNewConversation: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

// ---------------------------------------------------------------------------
// Date formatting helper
// ---------------------------------------------------------------------------

function formatRelativeDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 60) {
    return diffMin <= 0 ? "now" : `${diffMin}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConversationList({
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  isOpen,
  onToggle,
}: ConversationListProps) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [linkedPrompt, setLinkedPrompt] = useState<{
    conversationId: number;
    shortlistName: string | null;
  } | null>(null);
  const [deletingLinked, setDeletingLinked] = useState(false);

  // Fetch conversations when sidebar opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    listConversations()
      .then((data) => {
        if (!cancelled) setConversations(data);
      })
      .catch(() => {
        // Silently ignore -- sidebar is supplementary
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Handle delete
  const handleDelete = useCallback(
    async (e: React.MouseEvent, id: number) => {
      e.stopPropagation();
      try {
        await deleteConversation(id);
        setConversations((prev) => prev.filter((c) => c.id !== id));
        if (id === currentConversationId) {
          onNewConversation();
        }
      } catch (err) {
        if (err instanceof LinkedShortlistError) {
          setLinkedPrompt({
            conversationId: id,
            shortlistName: err.linkedShortlistName,
          });
        }
      }
    },
    [currentConversationId, onNewConversation]
  );

  const handleLinkedDelete = useCallback(
    async (alsoDeleteShortlist: boolean) => {
      if (!linkedPrompt) return;
      setDeletingLinked(true);
      try {
        await deleteConversation(
          linkedPrompt.conversationId,
          alsoDeleteShortlist,       // also_delete_shortlist
          !alsoDeleteShortlist,      // keep_shortlist (if not deleting it)
        );
        setConversations((prev) =>
          prev.filter((c) => c.id !== linkedPrompt.conversationId),
        );
        if (linkedPrompt.conversationId === currentConversationId) {
          onNewConversation();
        }
        setLinkedPrompt(null);
      } catch {
        // Silently fail
      } finally {
        setDeletingLinked(false);
      }
    },
    [linkedPrompt, currentConversationId, onNewConversation],
  );

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: isOpen ? 280 : 0,
        transition: "width 0.2s ease",
        background: "var(--surface-elevated)",
        borderRight: isOpen ? "1px solid var(--border)" : "none",
        zIndex: 10,
        overflow: isOpen ? "hidden" : "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div style={styles.header}>
        <button
          onClick={() => {
            onNewConversation();
            // Don't close sidebar -- let caller handle
          }}
          style={styles.newButton}
          aria-label="New conversation"
        >
          <Plus size={16} />
          <span>New conversation</span>
        </button>
        <button
          onClick={onToggle}
          style={styles.closeButton}
          aria-label="Close sidebar"
        >
          <X size={16} />
        </button>
      </div>

      {/* Conversation list */}
      <div style={styles.listContainer}>
        {conversations.length === 0 ? (
          <p style={styles.emptyText}>No past conversations</p>
        ) : (
          conversations.map((conv) => {
            const isActive = conv.id === currentConversationId;
            const isHovered = conv.id === hoveredId;

            return (
              <div
                key={conv.id}
                onClick={() => onSelectConversation(conv.id)}
                onMouseEnter={() => setHoveredId(conv.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  ...styles.row,
                  background: isActive
                    ? "color-mix(in srgb, var(--accent-blue) 12%, transparent)"
                    : isHovered
                      ? "color-mix(in srgb, var(--text-secondary) 8%, transparent)"
                      : "transparent",
                  cursor: "pointer",
                }}
              >
                <MessageSquare
                  size={14}
                  style={{
                    color: "var(--text-secondary)",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                />
                <div style={styles.rowContent}>
                  <span style={styles.title}>
                    {conv.title || "New conversation"}
                  </span>
                  <div style={styles.rowMeta}>
                    <span style={styles.date}>
                      {formatRelativeDate(conv.created_at)}
                    </span>
                    {conv.candidate_count > 0 && (
                      <>
                        <span style={styles.dot}>&middot;</span>
                        <span style={styles.date}>
                          {conv.candidate_count} candidate
                          {conv.candidate_count !== 1 ? "s" : ""}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {isHovered && (
                  <button
                    onClick={(e) => handleDelete(e, conv.id)}
                    style={styles.trashButton}
                    aria-label={`Delete conversation ${conv.title || conv.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Linked shortlist confirmation dialog */}
      {linkedPrompt && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            background: "rgba(0,0,0,0.5)",
          }}
          onClick={() => !deletingLinked && setLinkedPrompt(null)}
        >
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "12px",
              padding: "1.5rem",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 0.5rem",
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "var(--text-primary)",
              }}
            >
              Also delete linked shortlist?
            </h3>
            <p
              style={{
                margin: "0 0 1.25rem",
                fontSize: "0.9rem",
                color: "var(--text-secondary)",
                lineHeight: 1.5,
              }}
            >
              This conversation has a linked shortlist
              {linkedPrompt.shortlistName && (
                <> &ldquo;{linkedPrompt.shortlistName}&rdquo;</>
              )}
              . Do you want to delete the shortlist too?
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={() => setLinkedPrompt(null)}
                disabled={deletingLinked}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  background: "transparent",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleLinkedDelete(false)}
                disabled={deletingLinked}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  border: "1px solid var(--border)",
                  borderRadius: "8px",
                  background: "transparent",
                  color: "var(--text-primary)",
                  cursor: deletingLinked ? "wait" : "pointer",
                  opacity: deletingLinked ? 0.7 : 1,
                }}
              >
                Keep Shortlist
              </button>
              <button
                type="button"
                onClick={() => handleLinkedDelete(true)}
                disabled={deletingLinked}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  border: "none",
                  borderRadius: "8px",
                  background: "#ef4444",
                  color: "#fff",
                  cursor: deletingLinked ? "wait" : "pointer",
                  opacity: deletingLinked ? 0.7 : 1,
                }}
              >
                {deletingLinked ? "Deleting..." : "Delete Both"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.75rem",
    borderBottom: "1px solid var(--border)",
    gap: "0.5rem",
    flexShrink: 0,
  },
  newButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.4rem 0.7rem",
    borderRadius: "8px",
    border: "none",
    background: "transparent",
    color: "var(--text-primary)",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s",
    whiteSpace: "nowrap",
  },
  closeButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "6px",
    border: "none",
    background: "transparent",
    color: "var(--text-secondary)",
    cursor: "pointer",
    flexShrink: 0,
  },
  listContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "0.25rem 0",
  },
  emptyText: {
    color: "var(--text-secondary)",
    fontSize: "0.85rem",
    textAlign: "center" as const,
    padding: "2rem 1rem",
    margin: 0,
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    padding: "0.6rem 0.75rem",
    transition: "background 0.1s",
    position: "relative" as const,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.15rem",
  },
  title: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  rowMeta: {
    display: "flex",
    alignItems: "center",
    gap: "0.3rem",
    fontSize: "0.78rem",
  },
  date: {
    color: "var(--text-secondary)",
  },
  dot: {
    color: "var(--text-secondary)",
    fontSize: "0.7rem",
  },
  trashButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "4px",
    border: "none",
    background: "transparent",
    color: "var(--text-secondary)",
    cursor: "pointer",
    flexShrink: 0,
    marginTop: 2,
  },
};
